import { useEffect, useRef, useState } from "react";
import api, { downloadBlob, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Scale, Calculator, Download, Upload, FileSpreadsheet, AlertTriangle } from "lucide-react";

const usd = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString());

const DEFAULTS = {
  vrf: "High", vsl: "Severe", days: 1, violations: 1,
  entity_size: 1, assessed_risk: 1, time_horizon: 1,
  duration_pct: 0, repetitive_pct: 0, remedial_pct: 0,
  intentional: false, concealment: false, management: false,
  compliance_pct: 0, cooperation_pct: 0, self_report_pct: 0,
  settlement_pct: 0, extenuating: false, extenuating_pct: 0,
  disgorgement: 0, label: "",
};

const NUM = ({ label, hint, value, onChange, testid, min, max, step = "any" }) => (
  <div>
    <Label className="text-xs">{label}{hint && <span className="text-muted-foreground font-normal"> {hint}</span>}</Label>
    <Input type="number" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className="rounded-none h-9" data-testid={testid} />
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border border-border px-3 py-2 cursor-pointer bg-white">
    <span className="text-sm">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

export default function SanctionCalculator() {
  const { isSuperadmin } = useAuth();
  const [f, setF] = useState(DEFAULTS);
  const [cfg, setCfg] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [maxEdit, setMaxEdit] = useState("");
  const [upload, setUpload] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/sanction/config").then(({ data }) => { setCfg(data); setMaxEdit(String(data.statutory_max_daily)); }).catch(() => {});
  }, []);

  const baseCell = cfg?.base_matrix?.[f.vrf]?.[f.vsl];

  const calculate = async () => {
    setBusy("calc");
    try { const { data } = await api.post("/sanction/calculate", f); setRes(data); setUpload(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const exportResult = async () => {
    setBusy("export");
    try {
      const resp = await api.post("/sanction/calculate/export", f, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "ClaroShield-Sanction-Estimate.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const downloadTemplate = async () => {
    try { await downloadBlob("/sanction/template", "ClaroShield-Sanction-Calculator.xlsx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/sanction/upload", fd);
      setF((p) => ({ ...p, ...data.input })); setRes(data.result); setUpload(data.offline || {});
      toast.success("Template parsed and recalculated with today's tables");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveMax = async () => {
    try {
      const { data } = await api.put("/console/sanction/config", { statutory_max_daily: Number(maxEdit) });
      setCfg((c) => ({ ...c, statutory_max_daily: data.statutory_max_daily }));
      toast.success("Statutory daily maximum updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8 space-y-6" data-testid="sanction-page">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 border border-primary/20"><Scale className="text-primary" size={22} /></div>
        <div>
          <h1 className="text-2xl font-display font-bold">Sanction Guideline Calculator</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">Estimate a NERC monetary-penalty range using the Sanction Guidelines (Rules of Procedure, Appendix 4B). This is a planning estimate — actual penalties are set at the discretion of NERC / the Regional Entity.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-none" onClick={downloadTemplate} data-testid="sanction-download-template"><FileSpreadsheet size={15} className="mr-1.5" /> Download Excel template</Button>
        <Button variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} data-testid="sanction-upload-btn"><Upload size={15} className="mr-1.5" /> Upload filled template</Button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="sanction-upload-input" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-2 space-y-5">
          <section className="border border-border bg-white p-4 space-y-3">
            <h3 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">Step 1 · Base penalty</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Violation Risk Factor (VRF)</Label>
                <Select value={f.vrf} onValueChange={(v) => set("vrf", v)}>
                  <SelectTrigger className="rounded-none h-9" data-testid="sanction-vrf"><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg?.vrfs || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Violation Severity Level (VSL)</Label>
                <Select value={f.vsl} onValueChange={(v) => set("vsl", v)}>
                  <SelectTrigger className="rounded-none h-9" data-testid="sanction-vsl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg?.vsls || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {baseCell && <p className="text-xs text-muted-foreground">Base range for {f.vrf}/{f.vsl}: <span className="font-mono text-foreground">{usd(baseCell[0])} – {usd(baseCell[1])}</span> per violation, per day.</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              <NUM label="Number of days" value={f.days} onChange={(v) => set("days", v)} min={1} testid="sanction-days" />
              <NUM label="Number of violations" value={f.violations} onChange={(v) => set("violations", v)} min={1} testid="sanction-violations" />
              <NUM label="Entity size" hint="(0.25–6)" value={f.entity_size} onChange={(v) => set("entity_size", v)} min={0.25} max={6} testid="sanction-size" />
              <NUM label="Assessed risk" hint="(1–8)" value={f.assessed_risk} onChange={(v) => set("assessed_risk", v)} min={1} max={8} testid="sanction-risk" />
              <NUM label="Time horizon" hint="(1–4)" value={f.time_horizon} onChange={(v) => set("time_horizon", v)} min={1} max={4} testid="sanction-horizon" />
              <NUM label="Duration increase %" hint="(0–500)" value={f.duration_pct} onChange={(v) => set("duration_pct", v)} min={0} max={500} testid="sanction-duration" />
            </div>
          </section>

          <section className="border border-border bg-white p-4 space-y-3">
            <h3 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">Step 2 · Aggravating / mitigating</h3>
            <div className="grid sm:grid-cols-3 gap-2">
              <Toggle label="Intentional (×2)" checked={f.intentional} onChange={(v) => set("intentional", v)} testid="sanction-intentional" />
              <Toggle label="Concealment (×2)" checked={f.concealment} onChange={(v) => set("concealment", v)} testid="sanction-concealment" />
              <Toggle label="Mgmt involvement (×2)" checked={f.management} onChange={(v) => set("management", v)} testid="sanction-management" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <NUM label="Repetitive / history increase %" hint="(0–500)" value={f.repetitive_pct} onChange={(v) => set("repetitive_pct", v)} min={0} max={500} testid="sanction-repetitive" />
              <NUM label="Remedial-directive failure %" hint="(0–500)" value={f.remedial_pct} onChange={(v) => set("remedial_pct", v)} min={0} max={500} testid="sanction-remedial" />
              <NUM label="Compliance program reduction %" hint="(0–40)" value={f.compliance_pct} onChange={(v) => set("compliance_pct", v)} min={0} max={40} testid="sanction-compliance" />
              <NUM label="Cooperation reduction %" hint="(0–20)" value={f.cooperation_pct} onChange={(v) => set("cooperation_pct", v)} min={0} max={20} testid="sanction-cooperation" />
              <NUM label="Self-report reduction %" hint="(0–30)" value={f.self_report_pct} onChange={(v) => set("self_report_pct", v)} min={0} max={30} testid="sanction-selfreport" />
            </div>
          </section>

          <section className="border border-border bg-white p-4 space-y-3">
            <h3 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">Step 3 · Final adjustments</h3>
            <NUM label="Settlement reduction %" hint="(0–30 no-admit / 0–40 admit)" value={f.settlement_pct} onChange={(v) => set("settlement_pct", v)} min={0} max={40} testid="sanction-settlement" />
            <Toggle label="Was the non-compliance caused by a natural disaster or pandemic? (extenuating circumstances)" checked={f.extenuating} onChange={(v) => set("extenuating", v)} testid="sanction-extenuating" />
            {f.extenuating && (
              <div className="border-l-2 border-amber-400 pl-3">
                <NUM label="Extenuating reduction %" hint="(0–100 — may reduce the penalty to $0)" value={f.extenuating_pct} onChange={(v) => set("extenuating_pct", v)} min={0} max={100} testid="sanction-extenuating-pct" />
                <p className="text-[11px] text-amber-700 mt-1">Extenuating circumstances (e.g. natural disaster, pandemic) may significantly reduce or eliminate the penalty.</p>
              </div>
            )}
            <NUM label="Disgorgement of unjust profits ($)" value={f.disgorgement} onChange={(v) => set("disgorgement", v)} min={0} testid="sanction-disgorgement" />
            <div>
              <Label className="text-xs">Reference label (optional)</Label>
              <Input value={f.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="e.g. CIP-007-6 R2 self-report" className="rounded-none h-9" data-testid="sanction-label" />
            </div>
          </section>
        </div>

        {/* Result */}
        <div className="space-y-4">
          <div className="border border-border bg-white p-4 sticky top-4 space-y-3">
            <Button className="w-full rounded-none" onClick={calculate} disabled={busy === "calc"} data-testid="sanction-calculate"><Calculator size={16} className="mr-1.5" /> {busy === "calc" ? "Calculating…" : "Calculate range"}</Button>
            {res ? (
              <div data-testid="sanction-result" className="space-y-3">
                <div className="text-center py-3 bg-primary/5 border border-primary/15">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Estimated penalty range</div>
                  <div className="text-2xl font-display font-bold text-primary" data-testid="sanction-result-high">{usd(res.high)}</div>
                  <div className="text-xs text-muted-foreground">down to</div>
                  <div className="text-lg font-display font-semibold" data-testid="sanction-result-low">{usd(res.low)}</div>
                </div>
                {res.capped_at_statutory_max && <div className="flex items-start gap-1.5 text-[11px] text-amber-700"><AlertTriangle size={13} className="mt-0.5" /> Capped at the statutory daily maximum ({usd(res.statutory_max_daily)}/violation/day).</div>}
                <div className="text-[11px] text-muted-foreground space-y-0.5 font-mono">
                  <div>Base: {usd(res.base_range[0])}–{usd(res.base_range[1])}/day</div>
                  <div>Step-1 ×{res.breakdown.step1_multiplier} · {res.breakdown.days}d × {res.breakdown.violations} viol</div>
                  <div>Aggravating ×{res.breakdown.aggravating_factor} · Mitigating ×{res.breakdown.mitigating_factor}</div>
                  <div>Final adj ×{res.breakdown.final_adjustment_factor}{res.breakdown.disgorgement ? ` · +${usd(res.breakdown.disgorgement)} disgorge` : ""}</div>
                  {res.breakdown.extenuating_applied && <div className="text-amber-700">Extenuating circumstances applied</div>}
                </div>
                {upload && (upload.low != null) && (
                  <div className="text-[11px] border-t border-border pt-2">
                    <div className="text-muted-foreground">Your spreadsheet computed: {usd(upload.low)}–{usd(upload.high)}</div>
                    {(Math.abs(upload.high - res.high) > 1 || Math.abs(upload.low - res.low) > 1) && <div className="text-amber-700">⚠ Differs from today's tables — the figures above are authoritative.</div>}
                  </div>
                )}
                <Button variant="outline" className="w-full rounded-none" onClick={exportResult} disabled={busy === "export"} data-testid="sanction-export"><Download size={15} className="mr-1.5" /> Download estimate (Excel)</Button>
              </div>
            ) : <p className="text-xs text-muted-foreground text-center py-4">Enter the violation details and calculate to see the estimated range.</p>}
          </div>

          {isSuperadmin && (
            <div className="border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="sanction-admin-config">
              <div className="text-xs font-semibold text-amber-800">Super Admin · statutory daily maximum</div>
              <p className="text-[11px] text-amber-700">NERC adjusts this annually for inflation (18 CFR § 385.1602). Update it so calculations reflect today's program.</p>
              <div className="flex gap-2">
                <Input type="number" value={maxEdit} placeholder="1291894" onChange={(e) => setMaxEdit(e.target.value)} className="rounded-none h-8" data-testid="sanction-max-input" />
                <Button size="sm" className="rounded-none" onClick={saveMax} data-testid="sanction-max-save">Save</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
